// components/AI/AIReviewFindings.js — Tab "Review Findings" di AI Chat History (admin).
//
// Antrian jawaban AI Assistant yang patut direview: 👎 dari user + temuan scan
// otomatis (backend/aiReview.js: error, empty SQL, no-answer, edited, re-asked).
// Tahap 1 = review manual. Tahap berikutnya agent mingguan membaca antrian yang
// sama dan mengusulkan perbaikan (few-shot / hint / config / kode).
//
// Teks UI berbahasa Inggris, langsung di sini (bukan translations.js) — sama
// seperti tab Analytics/Cost Forecast, supaya gerbang paritas i18n tidak terpicu.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../context/AuthContext';
import AIImproveProposals from './AIImproveProposals';
import AIEvalCases, { CaseForm } from './AIEvalCases';
import {
  RefreshCw, Loader2, ThumbsUp, ThumbsDown, AlertTriangle, DatabaseZap, HelpCircle,
  Pencil, Repeat, ExternalLink, ChevronDown, ChevronUp, Check, X, RotateCcw, ScanSearch,
  FlaskConical, Bot, ListChecks, MessageSquareWarning, Scale,
} from 'lucide-react';

const KINDS = {
  thumbs_down: { label: 'Thumbs down', icon: ThumbsDown,    cls: 'bg-red-50 text-red-700 border-red-200' },
  complaint:   { label: 'Complaint',   icon: MessageSquareWarning, cls: 'bg-red-50 text-red-700 border-red-200' },
  judge:       { label: 'AI judge',    icon: Scale,         cls: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' },
  error:       { label: 'Error',       icon: AlertTriangle, cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  empty_sql:   { label: 'Empty SQL',   icon: DatabaseZap,   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  no_answer:   { label: 'No answer',   icon: HelpCircle,    cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  edited:      { label: 'Edited',      icon: Pencil,        cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  reask:       { label: 'Re-asked',    icon: Repeat,        cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};
const STATUSES = ['open', 'confirmed', 'fixed', 'dismissed', 'all'];
const STATUS_CLS = {
  open: 'bg-slate-100 text-slate-600', confirmed: 'bg-amber-100 text-amber-700',
  fixed: 'bg-emerald-100 text-emerald-700', dismissed: 'bg-slate-100 text-slate-400',
};

function fmtTime(s) {
  if (!s) return '-';
  const d = new Date(String(s).replace(' ', 'T') + (String(s).includes('Z') || String(s).includes('+') ? '' : 'Z'));
  return isNaN(d) ? s : d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function Kpi({ label, value, sub, tone = 'text-slate-800' }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Clamp({ text, lines = 3, mono = false }) {
  const [open, setOpen] = useState(false);
  if (!text) return <span className="text-slate-300">-</span>;
  const long = text.length > 260 || text.split('\n').length > lines;
  return (
    <div>
      <div className={`${mono ? 'font-mono text-[11px] bg-slate-50 rounded-lg p-2' : 'text-xs'} text-slate-700 whitespace-pre-wrap break-words`}
        style={!open && long ? { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : undefined}>
        {text}
      </div>
      {long && (
        <button onClick={() => setOpen(o => !o)} className="text-[11px] text-brand-600 hover:underline mt-0.5 flex items-center gap-0.5">
          {open ? <>Show less <ChevronUp style={{ width: 11, height: 11 }} /></> : <>Show more <ChevronDown style={{ width: 11, height: 11 }} /></>}
        </button>
      )}
    </div>
  );
}

function FindingCard({ f, onUpdate, sources }) {
  const [addCase, setAddCase] = useState(false);
  const [caseSaved, setCaseSaved] = useState(false);
  const k = KINDS[f.kind] || { label: f.kind, icon: HelpCircle, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  const Icon = k.icon;
  const [note, setNote] = useState(f.resolution_note || '');
  const [busy, setBusy] = useState(false);
  const set = async (status) => {
    setBusy(true);
    try { await onUpdate(f.id, { status, resolution_note: note || null }); } finally { setBusy(false); }
  };
  const openSession = () => {
    if (!f.session_id) return;
    window.open(`${window.location.origin}${window.location.pathname}?session=${encodeURIComponent(f.session_id)}`, '_blank', 'noopener');
  };
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-md px-1.5 py-0.5 ${k.cls}`}>
          <Icon style={{ width: 11, height: 11 }} /> {k.label}
        </span>
        <span className={`text-[11px] font-semibold rounded-md px-1.5 py-0.5 capitalize ${STATUS_CLS[f.status] || ''}`}>{f.status}</span>
        {f.dashboard && <span className="text-[11px] text-slate-500 bg-slate-50 rounded-md px-1.5 py-0.5">{f.dashboard}</span>}
        <span className="text-[11px] text-slate-400">{f.username} · {fmtTime(f.msg_at || f.created_at)}</span>
        <span className="text-[11px] text-slate-300 ml-auto">#{f.chat_id}</span>
      </div>

      <div className="grid gap-2">
        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Question</div>
          <Clamp text={f.question} lines={2} />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Answer</div>
          <Clamp text={f.answer} lines={3} />
        </div>
        {f.sql && (
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">SQL</div>
            <Clamp text={f.sql} lines={3} mono />
          </div>
        )}
        {f.detail && <div className="text-[11px] text-slate-500 italic">{f.detail}</div>}
        {f.correction && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2">
            <div className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-0.5">{f.kind === 'complaint' ? "User's follow-up message" : "User's correction"}</div>
            <div className="text-xs text-emerald-900 whitespace-pre-wrap">{f.correction}</div>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Resolution note (what was changed / why dismissed)"
          className="flex-1 min-w-[200px] text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400" />
        {f.status !== 'confirmed' && f.status !== 'fixed' && (
          <button disabled={busy} onClick={() => set('confirmed')}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 flex items-center gap-1 disabled:opacity-50">
            <AlertTriangle style={{ width: 12, height: 12 }} /> Confirm wrong
          </button>
        )}
        {f.status !== 'fixed' && (
          <button disabled={busy} onClick={() => set('fixed')}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1 disabled:opacity-50">
            <Check style={{ width: 12, height: 12 }} /> Fixed
          </button>
        )}
        {f.status !== 'dismissed' && (
          <button disabled={busy} onClick={() => set('dismissed')}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
            <X style={{ width: 12, height: 12 }} /> Dismiss
          </button>
        )}
        {f.status !== 'open' && (
          <button disabled={busy} onClick={() => set('open')}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
            <RotateCcw style={{ width: 12, height: 12 }} /> Reopen
          </button>
        )}
        <button onClick={() => setAddCase(a => !a)}
          className="text-xs px-2.5 py-1.5 rounded-lg text-sky-700 hover:bg-sky-50 flex items-center gap-1">
          <FlaskConical style={{ width: 12, height: 12 }} /> {caseSaved ? 'Added to test set' : 'Add to test set'}
        </button>
        <button onClick={openSession}
          className="text-xs px-2.5 py-1.5 rounded-lg text-brand-600 hover:bg-brand-50 flex items-center gap-1">
          <ExternalLink style={{ width: 12, height: 12 }} /> Session
        </button>
      </div>
      {addCase && (
        <div className="mt-3">
          <div className="text-[11px] text-slate-500 mb-1">
            Write the <b>correct</b> SQL for this question. The assistant's SQL is prefilled as a starting point — fix it before saving.
          </div>
          <CaseForm sources={sources} initial={{ question: f.question, expected_sql: f.sql || '', finding_id: f.id }}
            onSaved={() => { setAddCase(false); setCaseSaved(true); }} onCancel={() => setAddCase(false)} />
        </div>
      )}
    </div>
  );
}

function FindingsView() {
  const [sources, setSources] = useState([]);
  useEffect(() => {
    api.get('/api/ai/improve/status').then(r => setSources(r.data.sources || [])).catch(() => {});
  }, []);
  const [status, setStatus] = useState('open');
  const [kind, setKind] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scanMsg, setScanMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/ai/review-findings', { params: { status, kind: kind || undefined } });
      setData(res.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [status, kind]);
  useEffect(() => { load(); }, [load]);

  const [judging, setJudging] = useState(false);
  const judge = async () => {
    setJudging(true); setScanMsg('AI judge is reviewing the last 26 hours of answers…');
    try {
      await api.post('/api/ai/improve/judge-now', { hours: 26 });
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 4000));
        const st = (await api.get('/api/ai/improve/status')).data;
        if (!st.judge_running) { const r = st.last_judge_result || {}; setScanMsg(`AI judge: ${r.judged ?? 0} judged, ${r.flagged ?? 0} flagged`); break; }
      }
      await load();
    } catch (e) { setScanMsg(e.response?.data?.error || e.message); }
    finally { setJudging(false); }
  };
  const scan = async () => {
    setScanning(true); setScanMsg('');
    try {
      const res = await api.post('/api/ai/review-findings/scan', {});
      setScanMsg(res.data?.count ? `${res.data.count} new: ${res.data.diffText}` : 'No new findings');
      await load();
    } catch (e) { setScanMsg(e.response?.data?.error || e.message); }
    finally { setScanning(false); }
  };

  const update = async (id, body) => {
    await api.patch(`/api/ai/review-findings/${id}`, body);
    await load();
  };

  const byKind = data?.byKind || [];
  const count = (k, s) => byKind.filter(r => (!k || r.kind === k) && (s === 'all' || r.status === s))
    .reduce((a, r) => a + r.n, 0);
  const openTotal = count('', 'open');
  const items = data?.items || [];

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Open findings" value={openTotal} tone={openTotal ? 'text-amber-600' : 'text-emerald-600'} />
        <Kpi label="Confirmed wrong" value={count('', 'confirmed')} sub={`${count('', 'fixed')} fixed`} />
        <Kpi label="User feedback"
          value={<span className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-emerald-600"><ThumbsUp style={{ width: 15, height: 15 }} />{data?.feedback?.up ?? 0}</span>
            <span className="flex items-center gap-1 text-red-500"><ThumbsDown style={{ width: 15, height: 15 }} />{data?.feedback?.down ?? 0}</span>
          </span>} />
        <Kpi label="Last auto-scan" value={<span className="text-sm">{data?.lastScanAt ? fmtTime(data.lastScanAt) : 'never'}</span>}
          sub="Hourly scan · nightly AI judge 02:37" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${
              status === s ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {s} <span className="opacity-70">{count('', s)}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {scanMsg && <span className="text-[11px] text-slate-500">{scanMsg}</span>}
          <button onClick={scan} disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {scanning ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <ScanSearch style={{ width: 13, height: 13 }} />}
            Scan now
          </button>
          <button onClick={judge} disabled={judging} title="An AI auditor checks recent text-to-SQL answers for clear mistakes (runs nightly at 02:37)"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {judging ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <Scale style={{ width: 13, height: 13 }} />}
            Run AI judge
          </button>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50">
            <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        <button onClick={() => setKind('')}
          className={`text-[11px] px-2.5 py-1 rounded-md border ${!kind ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-600'}`}>
          All types
        </button>
        {Object.entries(KINDS).map(([key, k]) => {
          const n = count(key, status);
          const Icon = k.icon;
          return (
            <button key={key} onClick={() => setKind(key)}
              className={`text-[11px] px-2.5 py-1 rounded-md border flex items-center gap-1 ${
                kind === key ? 'bg-slate-800 border-slate-800 text-white' : `${k.cls}`}`}>
              <Icon style={{ width: 11, height: 11 }} /> {k.label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3 mb-3">{error}</div>}
      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-slate-400 p-6"><Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Loading…</div>
      )}
      {data && !items.length && (
        <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center">
          No findings for this filter.
        </div>
      )}
      <div className="grid gap-3">
        {items.map(f => <FindingCard key={f.id} f={f} onUpdate={update} sources={sources} />)}
      </div>
    </div>
  );
}

// Sub-tab: Findings (antrian temuan) | Proposals (usulan agent) | Test Set (regression).
export default function AIReviewFindings() {
  const [sub, setSub] = useState('findings');
  const [pending, setPending] = useState(0);
  const refreshCount = useCallback(() => {
    api.get('/api/ai/improve/status')
      .then(r => setPending(((r.data.proposals || []).find(x => x.status === 'pending') || {}).n || 0))
      .catch(() => {});
  }, []);
  useEffect(() => { refreshCount(); }, [refreshCount, sub]);
  const tab = (key, label, Icon, badge) => (
    <button onClick={() => setSub(key)}
      className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${
        sub === key ? 'bg-white shadow-sm text-brand-700 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}>
      <Icon style={{ width: 13, height: 13 }} /> {label}
      {badge ? <span className="text-[10px] bg-brand-600 text-white rounded-full px-1.5">{badge}</span> : null}
    </button>
  );
  return (
    <div>
      <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1 mb-4">
        {tab('findings', 'Findings', ListChecks)}
        {tab('proposals', 'Proposals', Bot, pending)}
        {tab('cases', 'Test Set', FlaskConical)}
      </div>
      {sub === 'findings' && <FindingsView />}
      {sub === 'proposals' && <AIImproveProposals onChanged={refreshCount} />}
      {sub === 'cases' && <AIEvalCases />}
    </div>
  );
}
